import React from "react";
import { Link } from "react-router-dom";
import { PageSEO } from "@/components/PageSEO";
import { CaroselloStudio } from "@/components/CaroselloStudio";
import { SEO } from "@/lib/seo";
import { TESTIMONIALS_DATA } from "@/lib/constants";
import { ArrowRight, Star, ShoppingCart } from "lucide-react";

/* Homepage riscritta su indicazioni della committente (giugno 2026).
   Struttura: hero -> contabilità -> 3 servizi -> Silvia e il team -> libro e recensioni -> contatti.
   Testi forniti dalla committente: non riscrivere o accorciare senza sua autorizzazione. */

const RITMO = [
  ["Ogni mese", "Contabilità aggiornata."],
  ["Ogni trimestre", "Analisi dell'andamento economico."],
  ["Durante l'anno", "Pianificazione fiscale."]
];

const SERVIZI = [
  {
    t: "Contabilità e pianificazione fiscale",
    d: "Una contabilità aggiornata ogni mese, accompagnata da analisi trimestrali e pianificazione fiscale. Per conoscere l'andamento della tua azienda durante l'anno e affrontare le scadenze con maggiore consapevolezza.",
    cta: "SCOPRI IL SERVIZIO",
    to: "/servizi",
    id: "contabilita"
  },
  {
    t: "Controllo di gestione e consulenza direzionale",
    d: "Andiamo oltre il risultato contabile per analizzare fatturato, margini, costi, flussi finanziari e indicatori di performance. Per comprendere come si formano i risultati e prendere decisioni su basi più solide.",
    cta: "APPROFONDISCI",
    to: "/controllo-di-gestione-micro-piccole-imprese",
    id: "controllo-di-gestione"
  },
  {
    t: "Pannello Direzionale",
    d: "Un cruscotto personale, aggiornato mensilmente, che organizza gli indicatori della tua impresa in modo semplice e consultabile. Attivabile anche da aziende che mantengono un altro commercialista, purché siano disponibili dati adeguati.",
    cta: "SCOPRI IL PANNELLO",
    to: "/pannello-direzionale",
    id: "pannello-direzionale"
  }
];

const RECENSIONI = [TESTIMONIALS_DATA[0], TESTIMONIALS_DATA[2], TESTIMONIALS_DATA[4]];

const HomePage = () => (
  <main data-testid="home-page">
    <PageSEO {...SEO.home} />

    {/* 1. HERO */}
    <section className="pt-28 pb-16 md:pt-36 md:pb-24 bg-[#FAF8F5]" data-testid="hero-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.08fr_0.92fr] gap-12 lg:gap-16 items-center">
        <div>
          <p className="text-gold font-bold tracking-[0.28em] text-xs mb-6">
            SC DOTTORI COMMERCIALISTI · RAVENNA, CLIENTI IN TUTTA ITALIA
          </p>
          <h1 className="mb-7">
            <span className="block font-heading text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.03] text-[#0D1527]">
              La tua azienda ha bisogno di numeri che ti aiutino a guardare avanti.
            </span>
            <span
              className="block text-base md:text-lg font-normal text-[#4A5568] mt-4 leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Non soltanto a raccontare il passato.
            </span>
          </h1>
          <p className="text-[#0D1527] font-semibold text-base md:text-lg border-l-2 border-gold pl-5 mb-8 leading-relaxed">
            Contabilità aggiornata. Pianificazione fiscale. Controllo di gestione.
          </p>
          <div className="space-y-4 max-w-xl mb-10">
            <p className="text-[#3B4658] leading-relaxed">
              Sono Silvia Casali, Dottore Commercialista e Revisore Legale.
            </p>
            <p className="text-[#3B4658] leading-relaxed">
              Con il mio team affianchiamo gli imprenditori che vogliono conoscere davvero i numeri della propria
              azienda, prevedere gli impegni fiscali e prendere decisioni con maggiore consapevolezza.
            </p>
            <p className="text-[#3B4658] leading-relaxed">
              Perché il bilancio non dovrebbe raccontarti soltanto come è andato l'anno scorso.
              Dovrebbe aiutarti a capire cosa fare domani.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/servizi"
              className="inline-flex items-center justify-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-colors whitespace-nowrap"
              data-testid="hero-cta-primary"
            >
              SCOPRI I NOSTRI SERVIZI <ArrowRight size={18} />
            </Link>
            <Link
              to="/contatti"
              className="inline-flex items-center justify-center gap-3 border border-[#0D1527]/25 text-[#0D1527] hover:border-gold hover:text-gold px-8 py-4 rounded font-bold tracking-wide transition-colors whitespace-nowrap"
              data-testid="hero-cta-secondary"
            >
              PARLIAMO DELLA TUA AZIENDA
            </Link>
          </div>
        </div>
        <figure className="mt-4 lg:mt-0">
          <img
            src="/home/silvia-casali.webp"
            alt="Silvia Casali, Dottore Commercialista e Revisore Legale, fondatrice di SC Dottori Commercialisti"
            width="900"
            height="1303"
            className="w-full max-w-md mx-auto lg:max-w-none rounded-lg border border-[#E8E2D5] shadow-[0_24px_60px_-32px_rgba(13,21,39,0.45)]"
            data-testid="hero-foto-silvia"
          />
          <figcaption className="text-[#64708A] text-sm mt-4 lg:text-right">
            <span className="font-bold text-[#0D1527]">Silvia Casali</span> — Dottore Commercialista e Revisore Legale
          </figcaption>
        </figure>
      </div>
    </section>

    {/* 2. LA NOSTRA DIFFERENZA COMINCIA DALLA CONTABILITÀ */}
    <section className="py-16 md:py-24 bg-[#F4EFEA] border-y border-[#E8E2D5]" data-testid="home-contabilita">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0D1527] leading-tight mb-8 max-w-3xl">
          La contabilità non dovrebbe servirti soltanto a sapere quante tasse pagare.
        </h2>
        <div className="max-w-2xl space-y-4">
          <p className="text-[#3B4658] leading-relaxed">
            La maggior parte delle decisioni importanti in un'azienda viene presa durante l'anno.
            Eppure molti imprenditori conoscono i propri risultati soltanto quando arriva il bilancio.
          </p>
          <p className="text-[#0D1527] font-semibold leading-relaxed">Ecco come lavoriamo.</p>
          <p className="text-[#3B4658] leading-relaxed">
            Teniamo la contabilità aggiornata mensilmente, perché i numeri devono essere disponibili quando servono.
            Ogni trimestre analizziamo l'andamento economico dell'impresa, attraverso un confronto in consulenza
            e/o un report. E affianchiamo a tutto questo la pianificazione fiscale, per aiutarti a conoscere e
            programmare gli impegni tributari.
          </p>
          <p className="text-[#0D1527] font-semibold leading-relaxed">
            Non devi aspettare la fine dell'anno per scoprire come sta andando la tua azienda.
          </p>
        </div>

        <div
          className="mt-12 bg-white border border-[#E8E2D5] rounded-lg divide-y md:divide-y-0 md:divide-x divide-[#E8E2D5] grid md:grid-cols-3"
          data-testid="home-ritmo-contabilita"
        >
          {RITMO.map(([quando, cosa]) => (
            <div key={quando} className="px-7 py-7">
              <p className="text-gold font-bold tracking-[0.2em] text-xs mb-2">{quando.toUpperCase()}</p>
              <p className="text-[#0D1527] text-base md:text-lg leading-snug">{cosa}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* 3. I NOSTRI SERVIZI */}
    <section className="py-16 md:py-24 bg-[#FAF8F5]" data-testid="home-servizi">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0D1527] leading-tight mb-8 max-w-3xl">
          Ogni impresa ha esigenze diverse. Noi partiamo dai suoi numeri.
        </h2>
        <div className="max-w-2xl space-y-4 mb-12">
          <p className="text-[#3B4658] leading-relaxed">
            C'è chi desidera una gestione contabile e fiscale più attenta. Chi vuole capire dove si formano davvero
            i margini e dove si perdono risorse. E chi vuole avere gli indicatori della propria azienda raccolti in
            un cruscotto aggiornato.
          </p>
          <p className="text-[#3B4658] leading-relaxed">
            Per questo offriamo servizi distinti, che possono essere attivati secondo le esigenze dell'impresa.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {SERVIZI.map((s) => (
            <article
              key={s.id}
              className="bg-white border border-[#E8E2D5] hover:border-gold/70 rounded-lg p-7 flex flex-col transition-colors"
              data-testid={`home-servizio-${s.id}`}
            >
              <h3 className="font-heading text-2xl text-[#0D1527] leading-snug mb-4">{s.t}</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed flex-1">{s.d}</p>
              <Link
                to={s.to}
                className="inline-flex items-center gap-2 text-gold font-bold text-sm mt-6 hover:gap-3 transition-all"
                data-testid={`home-servizio-cta-${s.id}`}
              >
                {s.cta} <ArrowRight size={15} />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>

    {/* 4. SILVIA CASALI E IL TEAM */}
    <section className="py-16 md:py-24 bg-[#0D1527]" data-testid="home-team">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
        <div className="lg:border-r lg:border-white/10 lg:pr-10">
          <p className="text-gold font-bold tracking-[0.28em] text-xs mb-4">LO STUDIO</p>
          <p className="font-heading text-3xl md:text-4xl text-white leading-[1.05]">
            SC DOTTORI<br />COMMERCIALISTI
          </p>
          <p className="text-white/50 text-sm mt-3">S.r.l. STP · Ravenna</p>
        </div>
        <div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white leading-tight mb-8">
            Un punto di riferimento. Un team di professionisti.
          </h2>
          <div className="space-y-4 max-w-2xl">
            <p className="text-white/80 leading-relaxed">
              Sono Silvia Casali, Dottore Commercialista e Revisore Legale.
            </p>
            <p className="text-white/80 leading-relaxed">
              Ho fondato SC Dottori Commercialisti con un'idea precisa: costruire uno studio che non si limitasse a
              gestire gli adempimenti, ma aiutasse gli imprenditori a comprendere i numeri della propria azienda.
            </p>
            <p className="text-white/80 leading-relaxed">
              Oggi questa visione è condivisa da un team di professionisti con competenze diverse, che lavorano in
              modo coordinato per offrire assistenza contabile, fiscale e gestionale.
            </p>
            <p className="text-gold font-semibold leading-relaxed">
              Il mio nome rappresenta il progetto. Il nostro team lo rende concreto ogni giorno.
            </p>
          </div>
          <Link
            to="/chi-siamo"
            className="inline-flex items-center gap-3 border border-gold/60 text-gold hover:bg-gold hover:text-black px-7 py-3.5 rounded font-bold tracking-wide transition-colors mt-10"
            data-testid="home-cta-chi-siamo"
          >
            CONOSCI IL NOSTRO STUDIO <ArrowRight size={17} />
          </Link>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 mt-12">
        <CaroselloStudio tono="scuro" testid="home-carosello-studio" />
      </div>
    </section>

    {/* 5. AUTOREVOLEZZA E FIDUCIA */}
    <section className="py-16 md:py-24 bg-[#FAF8F5]" data-testid="home-autorevolezza">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0D1527] leading-tight mb-12 max-w-3xl">
          La competenza conta. Ma conta anche saperla condividere.
        </h2>

        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-center">
          <Link to="/libro" className="block justify-self-center lg:justify-self-start" data-testid="home-libro-cover">
            <img
              src="/libro/copertina-dai-numeri-alle-decisioni.jpg"
              alt="Copertina del libro Dai numeri alle decisioni di Silvia Casali"
              width="300"
              height="450"
              className="w-56 md:w-64 rounded shadow-[0_24px_50px_-24px_rgba(13,21,39,0.5)]"
            />
          </Link>
          <div>
            <p className="text-gold font-bold tracking-[0.28em] text-xs mb-4">IL LIBRO</p>
            <p className="font-heading text-3xl md:text-4xl text-[#0D1527] leading-tight mb-5">
              Dai numeri alle decisioni
            </p>
            <div className="space-y-4 max-w-xl">
              <p className="text-[#3B4658] leading-relaxed">
                Ho scritto <em>Dai numeri alle decisioni</em> per aiutare gli imprenditori a comprendere alcuni numeri
                fondamentali della propria azienda e a utilizzarli per prendere decisioni migliori.
              </p>
              <p className="text-[#0D1527] font-semibold leading-relaxed">
                È lo stesso principio che guida ogni giorno il nostro lavoro.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link
                to="/libro"
                className="inline-flex items-center justify-center gap-3 border border-[#0D1527]/25 text-[#0D1527] hover:border-gold hover:text-gold px-7 py-3.5 rounded font-bold tracking-wide transition-colors"
                data-testid="home-libro-cta"
              >
                SCOPRI IL LIBRO <ArrowRight size={17} />
              </Link>
              <a
                href="https://amzn.eu/d/0bZF9PI2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 text-[#64708A] hover:text-gold px-2 py-3.5 font-semibold text-sm transition-colors"
                data-testid="home-libro-amazon"
              >
                <ShoppingCart size={16} /> Disponibile su Amazon
              </a>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-16" data-testid="home-recensioni">
          {RECENSIONI.map((t, i) => (
            <figure key={i} className="bg-white border border-[#E8E2D5] rounded-lg p-6" data-testid={`home-recensione-${i}`}>
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} size={13} className="text-gold fill-gold" />
                ))}
              </div>
              <blockquote className="text-[#3B4658] text-sm leading-relaxed mb-4">"{t.quote}"</blockquote>
              <figcaption className="text-[#64708A] text-xs">{t.sector}</figcaption>
            </figure>
          ))}
        </div>
        <p className="text-[#8B94A6] text-xs mt-5 max-w-2xl">
          Testimonianze raccolte con il consenso dei clienti e pubblicate in forma anonima, nel rispetto del segreto
          professionale.
        </p>
      </div>
    </section>

    {/* 6. CONTATTI */}
    <section className="py-16 md:py-24 bg-[#F4EFEA] border-t border-[#E8E2D5]" data-testid="home-contatti">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0D1527] leading-tight mb-8">
          La tua azienda ha una storia. Cominciamo dai suoi numeri.
        </h2>
        <div className="space-y-4 mb-10">
          <p className="text-[#3B4658] leading-relaxed">
            Ogni impresa è diversa. Per questo, prima di proporti un servizio, vogliamo capire come lavori, quali
            difficoltà incontri e quali informazioni vorresti avere per gestire meglio la tua attività.
          </p>
          <p className="text-[#3B4658] leading-relaxed">
            Che tu stia cercando un commercialista, un supporto per il controllo di gestione o un cruscotto
            direzionale, il primo passo è parlarne.
          </p>
          <p className="text-[#0D1527] font-semibold leading-relaxed">Raccontaci la tua azienda.</p>
        </div>
        <Link
          to="/contatti"
          className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-9 py-4 rounded font-bold tracking-wide transition-colors"
          data-testid="home-cta-contatti"
        >
          CONTATTACI <ArrowRight size={18} />
        </Link>
        <p className="text-[#8B94A6] text-sm mt-8">
          Preferisci partire dai tuoi numeri?{" "}
          <a href="/check-up-fiscale/" className="text-gold font-semibold hover:underline" data-testid="home-checkup-link">
            Fai il check-up fiscale gratuito
          </a>
          .
        </p>
      </div>
    </section>
  </main>
);

export { HomePage };
